import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { FeedbackDialogComponent } from '@jfw/angular/shared/common-components/feedback';
import { LanguageSwitcherComponent } from '@jfw/angular/shared/common-components/language-switcher';
import { Language } from '@jfw/types';
import { combineLatest, map, startWith, Subject, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../standalone/services/auth/auth.service';
import { LanguagePreferenceService } from '../../../standalone/services/language/language-preference.service';
import { ThemeService } from '../../../standalone/services/theme/theme.service';
import { ModuleSearchComponent } from './module-search/module-search.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    MatToolbarModule,
    RouterModule,
    ModuleSearchComponent,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatDividerModule,
    LanguageSwitcherComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private languagePreference = inject(LanguagePreferenceService);
  // EXT-5700: the switcher shows the language actually in use this session.
  public activeLanguage = this.languagePreference.activeLanguage;

  public onLanguageChange(language: Language): void {
    this.languagePreference.save(language);
  }
  public servedEnvironment = environment.envVersion;
  // EXT-6172: off in test/prod until the feature is announced.
  public enableFeedback = environment.enablePlatformFeedback;
  // EXT-6562: the account menu's Appearance setting (IT users only, see
  // ThemeService.isAvailable).
  public themeService = inject(ThemeService);

  public openFeedback(): void {
    this.dialog.open(FeedbackDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });
  }

  @Output() menuClick = new EventEmitter<void>();

  public signOutClick$ = new Subject<void>();

  public failedPhotoUrl = signal<string | null>(null);

  public onPhotoError(url: string | null | undefined): void {
    this.failedPhotoUrl.set(url ?? null);
  }

  private signOutResponse$ = this.signOutClick$.pipe(
    switchMap(() => this.authService.signOut()),
    startWith(null),
  );

  public vm$ = combineLatest([
    this.authService.firebaseUser$,
    this.authService.platformUser$,
    this.signOutResponse$,
  ]).pipe(
    map(([firebaseUser, portalUser]) => {
      const signedIn = !!firebaseUser && !!portalUser;
      return {
        signedIn: signedIn,
        displayName: portalUser?.displayName,
        photoUrl: firebaseUser?.photoURL,
      };
    }),
  );
}
